import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useDataSourceTemplatesList } from "~/features/dataSourceTemplates/hooks";
import { toDataSourceTemplateRowList } from "~/features/dataSourceTemplates/utils/transformers";
import { v2Route } from "~/v2/routes";
import { DataSourceTemplatesPresenter } from "./dataSourceTemplates.presenter";

const DEFAULT_PAGE_SIZE = 25;

export function DataSourceTemplatesContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.dataSourceTemplates);
  const { data, refetch } = useDataSourceTemplatesList();

  const excludeDeactivated = params.excludeDeactivated ?? true;

  const rows = useMemo(() => {
    const all = toDataSourceTemplateRowList(data.dataSourceTemplates);
    return excludeDeactivated ? all.filter((row) => !row.isDeactivated) : all;
  }, [data.dataSourceTemplates, excludeDeactivated]);

  // The text input is driven by local state for instant feedback; the URL is a
  // background mirror (sharable / restored on reload + back-forward). We adopt the
  // URL value into local state only on a genuine external change (back/forward,
  // shared link), so our own writes never clobber the in-progress value.
  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedDataSourceTemplate = params.selectedDataSourceTemplate ?? null;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next ? next : undefined, page: undefined }));
    },
    [setParams],
  );

  const onExcludeDeactivatedChange = useCallback(
    (next: boolean) => {
      setParams((prev) => ({ ...prev, excludeDeactivated: next ? undefined : false, page: undefined }));
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next })),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      })),
    [setParams],
  );

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const onSelect = useCallback(
    (id: string) => setParams((prev) => ({ ...prev, selectedDataSourceTemplate: id })),
    [setParams],
  );

  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedDataSourceTemplate: undefined })),
    [setParams],
  );

  return (
    <DataSourceTemplatesPresenter
      rows={rows}
      search={search}
      onSearchChange={onSearchChange}
      excludeDeactivated={excludeDeactivated}
      onExcludeDeactivatedChange={onExcludeDeactivatedChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onRefresh={onRefresh}
      selectedDataSourceTemplate={selectedDataSourceTemplate}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
